            import * as React from "react";
import { Dictionary } from "../../../../core/types/Dictionary";
import { IContributorItem } from "../../../../repertoire/types/IContributorItem";
import { IRepertoireField } from "../../../../repertoire/types/IRepertoireField";
import { IPortalShareSetConfiguration } from "../../../types/IPortalShareSetConfiguration";
import DisplayTable, { IDisplayTableProps } from "../../../sharedComponents/displayTable/DisplayTable";
import CarouselScrollButton from "../../../../repertoire/components/carouselScrollButton/CarouselScrollButton";
import ContributorsDataViewButton from "../../../../repertoire/works/components/contributorsDataViewButton/ContributorsDataViewButton";
import { IAgreementLink } from "../../../../repertoire/types/IAgreementLink";
import { SOCIETY_OF_LICENSE_LOOKUP } from "../../../../lookup/Consts";
import { searchLookupValueByCode } from "../../../../lookup/services/LookupHelpers";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";

const DEFAULT_SET_TYPE = "repertoire";

export interface IMembersPortalContributorsDataViewProps
    extends Omit<IDisplayTableProps, "tableContents" | "dataGridTableData"> {
    contributors: Dictionary<IContributorItem[]>;
    defaultBackOfficeShareSet?: string;
    defaultSetTypeGroup?: string;
    dataGridTableData: IRepertoireField[];
    shareSetsShownInPortal?: IPortalShareSetConfiguration[];
    lookupValues?: ILookupDictionary;
}


const MembersPortalContributorsDataView: React.FC<
    IMembersPortalContributorsDataViewProps
> = (props) => {
    const {
        contributors,
        dataGridTableData,
        shareSetsShownInPortal,
        lookupValues,
        defaultSetTypeGroup,
        defaultBackOfficeShareSet = DEFAULT_SET_TYPE,
        ...displayTableProps
    } = props;

    const hasConfig = !!shareSetsShownInPortal && shareSetsShownInPortal.length > 0;

    const [activeBackOfficeSet, setActiveBackOfficeSet] = React.useState<string>(hasConfig ? shareSetsShownInPortal[0].BackOfficeShareSet: defaultBackOfficeShareSet);

    React.useEffect(() => {
        if (hasConfig) {
            setActiveBackOfficeSet(shareSetsShownInPortal[0].BackOfficeShareSet);
        } else {
            setActiveBackOfficeSet(defaultBackOfficeShareSet);
        }
    }, [hasConfig, shareSetsShownInPortal, defaultBackOfficeShareSet]);

    const buildContributorsWithAgreementRows = React.useCallback(
        (items: IContributorItem[]): IContributorItem[] => {
            if (!items || items.length === 0) return [];

            const result: IContributorItem[] = [];

            items.forEach((contributor) => {
                const chain = contributor.agreementChain ?? [];

                if (!chain.length) {
                    result.push(contributor);
                    return;
                }
                const originalPerfTotal = (contributor.perfPercentage?.value ?? (contributor.performingShareToDisplay ? parseFloat(contributor.performingShareToDisplay.value) || 0 : 0)) || 0;

                const originalMechTotal = (contributor.mechPercentage?.value ?? (contributor.mechanicalShareToDisplay ? parseFloat(contributor.mechanicalShareToDisplay.value) || 0 : 0)) || 0;

                let assignedPerfTotal = 0;
                let assignedMechTotal = 0;

                chain.forEach((ageementChain: IAgreementLink) => {
                    const perfSource = ageementChain.performingShareToDisplay ?? ageementChain.performingShare ?? 0;
                    const mechSource = ageementChain.mechanicalShareToDisplay ?? ageementChain.mechanicalShare ?? 0;

                    const perfVal = typeof perfSource === "string" ? parseFloat(perfSource) || 0 : perfSource;
                    const mechVal = typeof mechSource === "string" ? parseFloat(mechSource) || 0 : mechSource;

                    assignedPerfTotal += perfVal;
                    assignedMechTotal += mechVal;
                });

                const remainingPerf = Math.max(0, originalPerfTotal - assignedPerfTotal);
                const remainingMech = Math.max(0, originalMechTotal - assignedMechTotal);

                const parentContributor = JSON.parse(JSON.stringify(contributor)) as IContributorItem;

                if (parentContributor.perfPercentage) parentContributor.perfPercentage.value = remainingPerf;
                if (parentContributor.mechPercentage) parentContributor.mechPercentage.value = remainingMech;
                if (parentContributor.performingShareToDisplay) parentContributor.performingShareToDisplay.value = remainingPerf.toString();
                if (parentContributor.mechanicalShareToDisplay) parentContributor.mechanicalShareToDisplay.value = remainingMech.toString();

                result.push(parentContributor);

                chain.forEach((ageementChain: IAgreementLink) => {

                    const chainedContributor: IContributorItem = JSON.parse(JSON.stringify(contributor));

                    chainedContributor.ipBaseNumber = ageementChain.ipBaseNumber ?? chainedContributor.ipBaseNumber;

                    if (chainedContributor.name) {
                        chainedContributor.name.value = ageementChain.name ?? ageementChain.ipName ?? chainedContributor.name.value;
                    }

                    if (chainedContributor.ipiNumber) {
                        chainedContributor.ipiNumber.value = ageementChain.ipiNumber ?? chainedContributor.ipiNumber.value;
                    }

                    if (ageementChain.agreementCategoryRoleCode && chainedContributor.role) {
                        chainedContributor.role.value = ageementChain.agreementCategoryRoleCode;
                    }

                    const perfSource = ageementChain.performingShareToDisplay ?? ageementChain.performingShare ?? 0;

                    const mechSource = ageementChain.mechanicalShareToDisplay ?? ageementChain.mechanicalShare ?? 0;

                    const perfVal = typeof perfSource === "string" ? parseFloat(perfSource) || 0 : perfSource;
                    const mechVal = typeof mechSource === "string" ? parseFloat(mechSource) || 0 : mechSource;

                    if (chainedContributor.perfPercentage) {
                        chainedContributor.perfPercentage.value = perfVal;
                    }
                    if (chainedContributor.mechPercentage) {
                        chainedContributor.mechPercentage.value = mechVal;
                    }

                    if (chainedContributor.performingShareToDisplay) {
                        chainedContributor.performingShareToDisplay.value = perfVal.toString();
                    }
                    if (chainedContributor.mechanicalShareToDisplay) {
                        chainedContributor.mechanicalShareToDisplay.value = mechVal.toString();
                    }

                    chainedContributor.perfSocietyAffiliation.value = ageementChain.perfSocietyAffiliation ?? ageementChain.societyAffiliation ?? chainedContributor.perfSocietyAffiliation.value;

                    chainedContributor.mechSocietyAffiliation.value = ageementChain.mechSocietyAffiliation ?? ageementChain.societyAffiliation ?? chainedContributor.mechSocietyAffiliation.value;

                    if (chainedContributor.source && chainedContributor.source.value !== undefined) {
                        chainedContributor.source.value = "Agreement";
                    }

                    chainedContributor.agreementChain = [];
                    result.push(chainedContributor);

                });
            });

            return result;
        },
        []
    );

    const calculateContributorRelationships = React.useCallback(
        (items: IContributorItem[]): void => {
            if (!items) return;

            items.forEach(c => {
                if (c.isEditingRelationships) return;
                let relationships = '';
                c.relationshipIDs?.forEach(r => {
                    const index = items.findIndex(x => Math.abs(x.workContributorID) === Math.abs(r));
                    if (index !== -1) {
                        relationships += 'T' + (index + 1) + ';';
                    }
                });
                c.relationships = relationships;
            });
            
            items.forEach(c => {
                if (c.isEditingRelationships) return;
                items
                    .filter(oc => Math.abs(oc.workContributorID) !== Math.abs(c.workContributorID))
                    .forEach(oc => {
                        if (oc.relationshipIDs?.some(x => Math.abs(x) === Math.abs(c.workContributorID))) {
                            const ocIndex = items.findIndex(x => Math.abs(x.workContributorID) === Math.abs(oc.workContributorID));
                            c.relationships += 'F' + (ocIndex + 1) + ';';
                        }
                    });
            });
        },
        []
    );

    const mapSocietyOfLicense = React.useCallback(
        (contributor: IContributorItem): IContributorItem => {
            const societyCode = contributor.societyOfLicense?.value?.toString();

            if (!societyCode || !lookupValues) {
                return contributor;
            }

            const societyDescription = searchLookupValueByCode(SOCIETY_OF_LICENSE_LOOKUP, lookupValues, societyCode);

            if (!societyDescription) {
                return contributor;
            }

            return {
                ...contributor,
                societyOfLicense: {...contributor.societyOfLicense, value: societyDescription}
            };
        },
        [lookupValues]
    );

    const tableContentsForActiveTab = React.useMemo(() => {
        const raw = contributors[activeBackOfficeSet] ?? [];
        const rawFiltered = raw.filter(c => c.setTypeGroup === defaultSetTypeGroup);
        const expanded = buildContributorsWithAgreementRows(rawFiltered).map(mapSocietyOfLicense);
        calculateContributorRelationships(expanded);
        return expanded;
    }, [contributors, activeBackOfficeSet, buildContributorsWithAgreementRows, calculateContributorRelationships, defaultSetTypeGroup, mapSocietyOfLicense]);

    return (
        <div className="buttonsCarouselView">
            {hasConfig && (
                <div className="form-group col-md-6 col-sm-6 col-xs-12 flexContent">
                    <CarouselScrollButton
                        distanceToScroll={100}
                        content={shareSetsShownInPortal.map((cfg) => (
                            <ContributorsDataViewButton
                                key={cfg.BackOfficeShareSet}
                                text={cfg.PortalDisplayName}
                                active={cfg.BackOfficeShareSet === activeBackOfficeSet}
                                onClick={() => setActiveBackOfficeSet(cfg.BackOfficeShareSet)}
                            />
                        ))}
                    />
                </div>
            )}

            <DisplayTable
                tableContents={tableContentsForActiveTab}
                dataGridTableData={dataGridTableData}
                showRowNumber={true}
                {...displayTableProps}
            />
        </div>
    );
};

export default MembersPortalContributorsDataView;
